<template>
    <div class="user-photo-upload">
        <div class="photo-section">
            <div class="photo-header">
                <h6 class="photo-title">
                    <i class="fas fa-camera mr-2"></i>
                    Foto do Usuário
                </h6>
            </div>

            <!-- Preview da Foto -->
            <div class="photo-preview-container">
                <div class="photo-preview">
                    <!-- Imagem de preview carregada -->
                    <img
                        v-if="value.photo"
                        :src="value.photo"
                        class="preview-image"
                        alt="Foto do usuário"
                    />
                    <img
                        v-else-if="previewImage"
                        :src="previewImage"
                        class="preview-image"
                        alt="Preview da foto"
                    />
                    <!-- Imagem existente do usuário -->
                    <!-- Imagem padrão -->
                    <img
                        v-else
                        src="@/assets/images/profile.png"
                        class="preview-image default-image"
                        alt="Foto padrão"
                    />

                    <!-- Overlay para upload -->
                    <div class="photo-overlay" @click="triggerFileInput">
                        <div class="overlay-content">
                            <i class="fas fa-camera upload-icon"></i>
                            <span class="upload-text">
                                {{ value.photo || previewImage ? 'Alterar Foto' : 'Adicionar Foto' }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Informações da foto -->
                <div class="photo-info" v-if="previewImage || value.photo">
                    <div class="info-item">
                        <i class="fas fa-info-circle mr-1"></i>
                        <small class="text-muted">
                            {{ previewImage ? 'Nova foto selecionada' : 'Foto atual' }}
                        </small>
                    </div>
                </div>
            </div>

            <!-- Input de arquivo (oculto) -->
            <input
                type="file"
                ref="fileInput"
                id="userPic"
                class="file-input"
                accept="image/*"
                @change="handleFileChange"
                style="display: none"
            />

            <!-- Botões de ação -->
            <div class="photo-actions">
                <button
                    type="button"
                    class="btn btn-upload"
                    @click="triggerFileInput"
                >
                    <i class="fas fa-upload mr-2"></i>
                    {{ value.photo || previewImage ? 'Alterar' : 'Selecionar' }}
                </button>

                <button
                    v-if="previewImage || value.photo"
                    type="button"
                    class="btn btn-remove"
                    @click="removePhoto"
                >
                    <i class="fas fa-trash mr-2"></i>
                    Remover
                </button>
            </div>

            <!-- Instruções -->
            <div class="photo-instructions">
                <div class="instruction-item">
                    <i class="fas fa-check text-success mr-1"></i>
                    <small>Formatos aceitos: JPG, PNG, GIF</small>
                </div>
                <div class="instruction-item">
                    <i class="fas fa-check text-success mr-1"></i>
                    <small>Tamanho máximo: 5MB</small>
                </div>
                <div class="instruction-item">
                    <i class="fas fa-lightbulb text-warning mr-1"></i>
                    <small>Recomendado: foto quadrada</small>
                </div>
            </div>

            <!-- Erro de upload -->
            <div v-if="uploadError" class="upload-error">
                <i class="fas fa-exclamation-circle mr-1"></i>
                {{ uploadError }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'UserPhotoUpload',
    props: {
        value: {
            type: Object,
            required: true
        },
        previewImage: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            uploadError: null,
            maxFileSize: 5 * 1024 * 1024,
            allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
        };
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        handleFileChange(event) {
            const file = event.target.files[0];

            if (!file) {
                return;
            }

            if (!this.validateFile(file)) {
                return;
            }

            this.createImagePreview(file);
        },

        validateFile(file) {
            this.uploadError = null;

            if (!this.allowedTypes.includes(file.type)) {
                this.uploadError = 'Tipo de arquivo não suportado. Use JPG, PNG ou GIF.';
                return false;
            }

            if (file.size > this.maxFileSize) {
                this.uploadError = 'Arquivo muito grande. O tamanho máximo é 5MB.';
                return false;
            }

            return true;
        },

        createImagePreview(file) {
            const reader = new FileReader();

            reader.onload = (e) => {
                this.$emit('upload-image', e.target.result);
                this.uploadError = null;
            };

            reader.onerror = () => {
                this.uploadError = 'Erro ao carregar a imagem. Tente novamente.';
            };

            reader.readAsDataURL(file);
        },

        removePhoto() {
            this.$emit('upload-image', null);

            this.$emit('update', 'photo', null);

            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }

            this.uploadError = null;
        }
    }
};
</script>

<style scoped>
.user-photo-upload {
    display: flex;
    flex-direction: column;
}

.photo-section {
    background-color: #ffffff;
    border-radius: 8px;
    border: 1px solid #e9ecef;
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.photo-header {
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid #e9ecef;
}

.photo-title {
    color: #495057;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    font-size: 1rem;
}

.photo-preview-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 20px;
}

.photo-preview {
    position: relative;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid #e9ecef;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    background-color: #f8f9fa;
}

.photo-preview:hover {
    border-color: #FE8D18;
    transform: scale(1.02);
    box-shadow: 0 6px 20px rgba(0, 123, 255, 0.2);
}

.preview-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.default-image {
    opacity: 0.7;
}

.photo-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    cursor: pointer;
}

.photo-preview:hover .photo-overlay {
    opacity: 1;
}

.overlay-content {
    text-align: center;
    color: white;
}

.upload-icon {
    font-size: 24px;
    margin-bottom: 8px;
    display: block;
}

.upload-text {
    font-size: 12px;
    font-weight: 500;
    display: block;
}

.photo-info {
    margin-top: 10px;
    text-align: center;
}

.info-item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.photo-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 15px;
}

.btn {
    border-radius: 6px;
    font-weight: 500;
    padding: 8px 16px;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-upload {
    background: linear-gradient(135deg, #FE8D18 0%, #0056b3 100%);
    color: white;
}

.btn-upload:hover {
    background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.btn-remove {
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
    color: white;
}

.btn-remove:hover {
    background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
}

.photo-instructions {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 6px;
    padding: 12px;
    border: 1px solid #dee2e6;
}

.instruction-item {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    font-size: 11px;
}

.instruction-item:last-child {
    margin-bottom: 0;
}

.upload-error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 12px;
    margin-top: 10px;
    display: flex;
    align-items: center;
}

.text-success {
    color: #28a745 !important;
}

.text-warning {
    color: #ffc107 !important;
}

.text-muted {
    color: #6c757d !important;
}

/* Responsividade */
@media (max-width: 768px) {
    .photo-section {
        padding: 15px;
    }

    .photo-preview {
        width: 150px;
        height: 150px;
    }

    .upload-icon {
        font-size: 20px;
    }

    .upload-text {
        font-size: 11px;
    }

    .btn {
        padding: 10px 16px;
        font-size: 13px;
    }
}

@media (max-width: 576px) {
    .photo-preview {
        width: 120px;
        height: 120px;
    }

    .photo-title {
        font-size: 0.95rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/User/form-components/UserPhotoUpload.vue